import React from 'react';
import { Car, CheckCircle, MapPin, Phone, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const LocationVTCIDF = () => {
  const phoneNumber = "0650972620";
  const telLink = "tel:+33650972620";

  const handleCallClick = (e) => {
    if (window.innerWidth >= 768) {
      e.preventDefault();
      alert(`Appelez-nous au ${phoneNumber.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-sm z-50 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Car className="w-8 h-8 text-green-500" />
              <span className="text-xl font-bold">Location VTC Île-de-France</span>
            </Link>
            <div className="flex items-center gap-3">
              <a 
                href={telLink} 
                onClick={handleCallClick}
                className="hidden sm:inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 px-6 py-2 rounded-full font-semibold transition"
              >
                <Phone className="w-4 h-4" />
                Appeler
              </a>
              <Link to="/#contact" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full font-semibold transition border border-white/20">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Floating Call Button (Mobile Only) */}
      <a
        href={telLink}
        className="sm:hidden fixed bottom-6 right-6 z-40 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Appeler maintenant"
      >
        <Phone className="w-6 h-6" />
      </a>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Location VTC en <span className="text-green-500">Île-de-France</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8">
            Spécialiste de la location de véhicules VTC pour chauffeurs professionnels en Île-de-France
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-invert max-w-none">
            <h2 className="text-3xl font-bold mb-6 text-white">Location de voiture VTC en Île-de-France</h2>
            <p className="text-gray-300 text-lg mb-6">
              Vous êtes chauffeur VTC et vous recherchez une <strong>location VTC en Île-de-France</strong> ? Notre entreprise est spécialisée dans la <strong>location de voiture VTC</strong> pour les chauffeurs professionnels travaillant avec Uber, Bolt, Heetch ou FreeNow dans toute la région parisienne.
            </p>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Pourquoi choisir notre location VTC IDF ?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <h4 className="font-bold text-lg mb-2">Véhicules récents</h4>
                <p className="text-gray-400">Toyota C-HR hybrides parfaitement adaptés à l'activité VTC</p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <h4 className="font-bold text-lg mb-2">Kilométrage illimité</h4>
                <p className="text-gray-400">Roulez sans limite dans toute l'Île-de-France</p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <h4 className="font-bold text-lg mb-2">Sans caution</h4>
                <p className="text-gray-400">Démarrez votre activité sans blocage de capital</p>
              </div>
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <h4 className="font-bold text-lg mb-2">Économique</h4>
                <p className="text-gray-400">Véhicules hybrides pour réduire vos coûts</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Zone de service : Toute l'Île-de-France</h3>
            <p className="text-gray-300 text-lg mb-6">
              Notre service de <strong>location voiture VTC</strong> couvre l'ensemble de l'Île-de-France :
            </p>
            <ul className="space-y-2 text-gray-300 mb-8">
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Paris (75)</strong> - Location VTC dans tous les arrondissements</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Seine-Saint-Denis (93)</strong> - Location voiture VTC</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Hauts-de-Seine (92)</strong> - Véhicules VTC disponibles</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Val-de-Marne (94)</strong> - Location VTC professionnelle</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Seine-et-Marne (77)</strong> - Service de location VTC</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Yvelines (78)</strong> - Location voiture VTC</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Essonne (91)</strong> - Véhicules VTC économiques</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <span><strong>Val-d'Oise (95)</strong> - Location VTC sans caution</span>
              </li>
            </ul>

            <h3 className="text-2xl font-bold mb-4 text-white mt-12">Compatible avec toutes les plateformes VTC</h3>
            <p className="text-gray-300 text-lg mb-6">
              Nos véhicules sont parfaitement adaptés pour travailler avec :
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gray-800/50 p-4 rounded-lg text-center border border-gray-700">
                <p className="font-bold">Uber</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-lg text-center border border-gray-700">
                <p className="font-bold">Bolt</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-lg text-center border border-gray-700">
                <p className="font-bold">Heetch</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-lg text-center border border-gray-700">
                <p className="font-bold">FreeNow</p>
              </div>
            </div>

            <div className="bg-green-500/10 border border-green-500 rounded-xl p-8 my-12 text-center">
              <h3 className="text-2xl font-bold mb-4">Prêt à démarrer votre activité VTC en Île-de-France ?</h3>
              <p className="text-gray-300 mb-6">Contactez-nous dès maintenant pour louer votre véhicule VTC</p>
              <Link to="/#contact" className="inline-flex items-center bg-green-500 hover:bg-green-600 px-8 py-4 rounded-full font-semibold text-lg transition">
                Nous contacter <ChevronRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center space-x-2 mb-4">
            <Car className="w-6 h-6 text-green-500" />
            <span className="font-bold">Location VTC Île-de-France</span>
          </Link>
          <p className="text-gray-400 text-sm">
            Location VTC en Île-de-France | Location voiture VTC | Véhicules pour chauffeurs professionnels
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LocationVTCIDF;
